import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  clearSession,
  getSession,
  normalizeAuthResponse,
  saveSession,
} from "@/lib/auth-session";
import { clearLicenseStatus } from "@/lib/license";
import { setSessionInvalidatedHandler } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentVersion = process.env.REACT_APP_VERSION;
    const storedVersion = localStorage.getItem("tyretrack_version");

    if (currentVersion && storedVersion && storedVersion !== currentVersion) {
      console.log(`Versione cambiata da ${storedVersion} a ${currentVersion}. Logout forzato.`);
      clearSession();
      localStorage.setItem("tyretrack_version", currentVersion);
      setLoading(false);
      return;
    }

    if (currentVersion && !storedVersion) {
      localStorage.setItem("tyretrack_version", currentVersion);
    }
    setSession(getSession());
    setLoading(false);
  }, []);

  const login = useCallback((authResponse) => {
    const nextSession = normalizeAuthResponse(authResponse);
    saveSession(nextSession);
    setSession(nextSession);
    if (process.env.REACT_APP_VERSION) {
      localStorage.setItem("tyretrack_version", process.env.REACT_APP_VERSION);
    }
  }, []);

  const logout = useCallback(() => {
    clearSession();
    clearLicenseStatus();
    setSession(null);
  }, []);

  useEffect(() => {
    setSessionInvalidatedHandler(logout);
    return () => setSessionInvalidatedHandler(null);
  }, [logout]);

  const isAuthenticated = !!session;

  return (
    <AuthContext.Provider
      value={{ user: session?.user ?? null, login, logout, isAuthenticated, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
